import { faExclamationTriangle, faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import MaterialsButton from 'src/components/MaterialsButton';
import { INVENTORY_ACTION_TYPES } from 'src/constants/inventory';
import ScanBtn from 'src/pages/run/[uuid]/sections/ScanBtn';
import { getRouteURI, getUuid } from 'src/utils/url';

import routes from '../../../../utils/routes';
import WorkInstructions from './_work-instructions';

const ExtractDocumentDetailsButton = ({
  parserUri,
  documentUri,
  workInstructionReportUri,
  onExtract,
}) => {
  if (!parserUri || !documentUri) return null;

  return (
    <button
      className="btn btn-danger w-100"
      type="button"
      onClick={() => onExtract(parserUri, documentUri, workInstructionReportUri)}
    >
      Extract Document Details
    </button>
  );
};

const PrintWithWorkInstructions = ({
  print,
  piece,
  lineItem,
  model,
  checklist,
  reportValuesByUUID,
  onStepInput,
  onStepChange,
  uploadingState,
  sendNCReviewForPiece,
  runTransformation,
  setSentToNCRItems,
  sentToNCRItems,
  savedReportsByPrintUri,
  hasIncorrectValues,
  savedReportValues,
  setIsAbleToCompleteRun,
  reportFilesPrintURI,
  isFileUploading,
  label,
  handleExtractDocumentDetails,
  isServiceProvider,
  documentsByReportDataUri,
  documentState,
  handleActiveInstructionChange,
  onScanInventory,
  stockActions,
  onMaterialsClick,
  runStockActionsLoading,
  groupIndex,
  workInstructionReports,
  isInstructionSubmitting,
  isHighlighted,
}) => {
  const { currentDocumentUri } = documentState
  const reportFilesByPrint = reportFilesPrintURI[print.uri] || {};
  const documentParserUri = documentsByReportDataUri?.[currentDocumentUri]?.parser_template;
  const [activeInstructionIndex, setActiveInstructionIndex] = useState(0);

  // Calculate Materials button badge for individual piece
  // Only count INDIVIDUAL assignments (metadata.pieces.length === 1)
  const pieceStockCount = useMemo(() => {
    if (!stockActions || !piece?.uri) return 0;

    const uniqueStocks = new Set();
    stockActions.forEach(action => {
      const actionPieces = action.metadata?.pieces || [];
      // Only count INDIVIDUAL assignments (length === 1)
      if (actionPieces.length === 1 && actionPieces[0] === piece.uri) {
        uniqueStocks.add(action.source_stock);
      }
    });

    return uniqueStocks.size;
  }, [stockActions, piece?.uri]);
  const currentWorkInstruction = checklist?.work_instructions[activeInstructionIndex];
  const currentWorkInstructionReportUri = useMemo(() => {
    const reportByInstruction = {};

    workInstructionReports
      .sort((a, b) => new Date(a.completed) - new Date(b.completed))
      .forEach((report) => {
        reportByInstruction[report.work_instruction] = report;
      });

    return reportByInstruction[currentWorkInstruction?.uuid]?.uri;
  }, [JSON.stringify(workInstructionReports), JSON.stringify(currentWorkInstruction)])

  return (
    <div className={`work-instructions-container${isHighlighted ? ' scanned-piece' : ''}`}>
      {
        // "no-gutters" - padding is removed to cover WI content width decrease below
      }
      {model && (
        <div className={`${model ? 'w-50' : ''}`}>
          <div className="mb-4">
            {/* your thumbnail */}
            <div className="position-relative">
              <div className="thumbnail-box rounded-sm">
                <div className="thumbnail-box-inside">
                  <img
                    src={model.snapshot_content}
                    alt=""
                    className="img-fluid"
                  />
                </div>
              </div>
              <div className="floating-buttons-container">
                <ScanBtn
                  className="floating-inventory-scan-primary"
                  ariaLabel={`Scan QR code for ${piece?.name || 'piece'}`}
                  onScan={() => onScanInventory(INVENTORY_ACTION_TYPES.PIECE, print?.uri, groupIndex)}
              />
                {pieceStockCount > 0 && (
                  <MaterialsButton
                    isLoading={runStockActionsLoading}
                    count={pieceStockCount}
                    className="floating-inventory-scan-secondary"
                    onClick={() => onMaterialsClick([piece.uri], false, groupIndex, piece.name)}
                  />
                )}
              </div>
            </div>

          </div>
          {documentParserUri && currentDocumentUri && (<ExtractDocumentDetailsButton
            parserUri={documentParserUri}
            documentUri={currentDocumentUri}
            workInstructionReportUri={currentWorkInstructionReportUri}
            onExtract={handleExtractDocumentDetails}
          />)}
        </div>
      )}
      <div className="col-7 work-instructions-body-content">
        {
          // margin is needed for `Next` button to fit into the page
          // (issue is visible on screens < 600px)
        }
        <div className="mr-3 work-instructions-body-content-text">
          <div>Order: {print.name}</div>
          <div>Line Item: <span className="line-item-instructions">{lineItem && lineItem.name}</span></div>
          {piece && piece.name && (
            <div className={isServiceProvider ? 'd-flex flex-row justify-content-center' : ''}>
              Piece:&nbsp;
              {isServiceProvider ? (
                <p className="mb0">{piece.name}</p>
              ) : (
                <>
                  <Link className="word-break" to={getRouteURI(routes.pieceOverview, { uuid: getUuid(piece.uri) })}>
                    {piece.name}
                  </Link>
                  <Link to={getRouteURI(routes.pieceOverview, { uuid: getUuid(piece.uri) })}>
                    <FontAwesomeIcon className="link-icon" icon={faLink} />
                  </Link>
                </>
              )}
              {isHighlighted && !model && <span className="scanned-piece-badge">Scanned</span>}
              {label && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip>
                      <b>{label.name}</b>
                      <p>{label.description}</p>
                    </Tooltip>
                  }
                >
                  <span className="ml-1 warning-color">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  </span>
                </OverlayTrigger>
              )}
            </div>
          )}

          {/* Pice overview link  */}
          {checklist ? (
            <WorkInstructions
              instructions={checklist.work_instructions}
              values={reportValuesByUUID}
              savedValues={savedReportValues}
              uploading={uploadingState}
              beforeChange={onStepChange}
              print={print}
              sendNCReviewForPiece={sendNCReviewForPiece}
              runTransformation={runTransformation}
              setSentToNCRItems={setSentToNCRItems}
              sentToNCRItems={sentToNCRItems}
              savedReportsByPrintUri={savedReportsByPrintUri}
              hasIncorrectValues={hasIncorrectValues}
              setIsAbleToCompleteRun={setIsAbleToCompleteRun}
              reportFilesByPrint={reportFilesByPrint}
              isFileUploading={isFileUploading}
              handleActiveInstructionChange={handleActiveInstructionChange}
              setActiveInstructionIndex={setActiveInstructionIndex}
              documentsByReportDataUri={documentsByReportDataUri}
              isInstructionSubmitting={isInstructionSubmitting}
              onStepInput={onStepInput}
            />

          ) : (
            <div>No Work Instructions for the Piece</div>
          )}

          {!model && currentDocumentUri && documentParserUri && (
            <div className='work-instruction'>
              <ExtractDocumentDetailsButton
                parserUri={documentParserUri}
                documentUri={currentDocumentUri}
                workInstructionReportUri={currentWorkInstructionReportUri}
                onExtract={handleExtractDocumentDetails}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PrintWithWorkInstructions.propTypes = {
  print: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    piece: PropTypes.string.isRequired,
  }).isRequired,
  piece: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  lineItem: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  model: PropTypes.shape({
    name: PropTypes.string.isRequired,
    // eslint-disable-next-line camelcase
    snapshot_content: PropTypes.string.isRequired,
  }),
  checklist: PropTypes.shape({
    // eslint-disable-next-line camelcase
    work_instructions: PropTypes.arrayOf(PropTypes.shape({
      // eslint-disable-next-line camelcase
      parser_template: PropTypes.string,
      uuid: PropTypes.string.isRequired,
    })),
  }),
  reportValuesByUUID: PropTypes.shape({}),
  onStepInput: PropTypes.func.isRequired,
  onStepChange: PropTypes.func.isRequired,
  uploadingState: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})]),
  sendNCReviewForPiece: PropTypes.func.isRequired,
  runTransformation: PropTypes.arrayOf(PropTypes.shape({})),
  setSentToNCRItems: PropTypes.func.isRequired,
  sentToNCRItems: PropTypes.arrayOf(PropTypes.string),
  savedReportsByPrintUri: PropTypes.shape({}).isRequired,
  hasIncorrectValues: PropTypes.bool.isRequired,
  savedReportValues: PropTypes.shape({}).isRequired,
  setIsAbleToCompleteRun: PropTypes.func.isRequired,
  reportFilesPrintURI: PropTypes.shape({}).isRequired,
  isFileUploading: PropTypes.bool.isRequired,
  label: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  isServiceProvider: PropTypes.bool.isRequired,
  handleExtractDocumentDetails: PropTypes.func,
  documentsByReportDataUri: PropTypes.shape({}),
  documentState: PropTypes.shape({
    currentDocumentUri: PropTypes.string,
  }),
  handleActiveInstructionChange: PropTypes.func,
  onScanInventory: PropTypes.func.isRequired,
  stockActions: PropTypes.arrayOf(PropTypes.shape({})),
  onMaterialsClick: PropTypes.func,
  runStockActionsLoading: PropTypes.bool,
  groupIndex: PropTypes.number,
  workInstructionReports: PropTypes.arrayOf(PropTypes.shape({})),
  isInstructionSubmitting: PropTypes.bool.isRequired,
  isHighlighted: PropTypes.bool,
};

PrintWithWorkInstructions.defaultProps = {
  model: null,
  reportValuesByUUID: {},
  uploadingState: false,
  checklist: null,
  runTransformation: [],
  label: null,
  handleExtractDocumentDetails: null,
  documentsByReportDataUri: {},
  documentState: null,
  handleActiveInstructionChange: null,
  stockActions: [],
  onMaterialsClick: null,
  runStockActionsLoading: false,
  groupIndex: null,
  isHighlighted: false,
};

ExtractDocumentDetailsButton.propTypes = {
  parserUri: PropTypes.string,
  documentUri: PropTypes.string,
  workInstructionReportUri: PropTypes.string,
  onExtract: PropTypes.func.isRequired,
};

ExtractDocumentDetailsButton.defaultProps = {
  parserUri: null,
  documentUri: null,
};


export default PrintWithWorkInstructions;
